import { useMemo } from "react";
import invariant from "tiny-invariant";
import { InputFilterComparator } from "@/react/components/shared/Filter/FilterTypes/InputFilterComparator/InputFilterComparatorV2";
import type { StringComparatorParam } from "@/react/components/shared/Filter/FilterTypes/InputFilterComparator/stringComparators";
import {
  isStringComparatorParam,
  renderTextSelectedItem,
} from "@/react/components/shared/Filter/FilterTypes/InputFilterComparator/stringComparators";
import type { BaseFilter } from "@/react/components/shared/MemberFilters/types";
import { Filter } from "@circle-react-shared/FilterV3";
import { FilterChip } from "../../shared/FilterV3/FilterChipV2";
import { useFilterProvider } from "../FilterProvider";
import type { StringFilterConfiguration } from "../types";

interface StringFilterInputProps {
  configuration: StringFilterConfiguration;
}

// This component is not used in the FilterKit yet so implementation might not be correct
export const StringFilterInput = ({
  configuration,
}: StringFilterInputProps) => {
  const { filterParamForKey, setFilter, clearFilterByKey } =
    useFilterProvider();

  const param = useMemo((): StringComparatorParam => {
    const existingFilter = filterParamForKey(configuration.key) as
      | StringComparatorParam
      | undefined;

    return {
      id: configuration.key,
      key: configuration.key,
      value: (existingFilter?.value as string) ?? "",
      filter_type: existingFilter?.filter_type ?? "contains",
    };
  }, [filterParamForKey, configuration.key]);

  const handleApply = (value: BaseFilter | null) => {
    if (value === null) {
      clearFilterByKey(configuration.key);
    } else {
      invariant(
        isStringComparatorParam(value),
        "Invalid string comparator param",
      );
      setFilter(configuration.key, {
        ...value,
        search_key: configuration.search_key,
      });
    }
  };

  return (
    <Filter onApply={handleApply}>
      <FilterChip renderSelectedValue={() => renderTextSelectedItem(param)}>
        {configuration.label}
      </FilterChip>
      <Filter.Popover title={configuration.label}>
        <InputFilterComparator
          name={configuration.key}
          param={param}
          placeholder={`Search by ${configuration.label.toLowerCase()}...`}
        />
      </Filter.Popover>
    </Filter>
  );
};
